import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  PlayArrow,
  ContentCopy,
  Visibility,
  Timer,
  QuestionAnswer,
  School,
  EmojiEvents,
  FilterList,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const ExamBrowser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [myExams, setMyExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Filtros
  const [filters, setFilters] = useState({
    area: 'todas',
    exam_type: '',
    difficulty_level: '',
  });

  // Áreas
  const areas = [
    { value: 'todas', label: 'Todas las Áreas' },
    { value: 'matematicas', label: 'Matemáticas' },
    { value: 'lectura_critica', label: 'Lectura Crítica' },
    { value: 'sociales', label: 'Sociales y Ciudadanas' },
    { value: 'ciencias', label: 'Ciencias Naturales' },
    { value: 'ingles', label: 'Inglés' },
  ];

  const examTypes = [
    { value: '', label: 'Todos los tipos' },
    { value: 'official', label: 'Oficial' },
    { value: 'practice', label: 'Práctica' },
    { value: 'teacher', label: 'Profesor' },
    { value: 'custom', label: 'Personalizado' },
  ];

  const difficultyLevels = [
    { value: '', label: 'Todas las dificultades' },
    { value: 'easy', label: 'Fácil' },
    { value: 'medium', label: 'Medio' },
    { value: 'hard', label: 'Difícil' },
    { value: 'mixed', label: 'Mixto' },
  ];

  useEffect(() => {
    fetchExams();
    if (user.role === 'teacher' || user.role === 'admin') {
      fetchMyExams();
    }
  }, [filters]);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.area !== 'todas') params.append('area', filters.area);
      if (filters.exam_type) params.append('exam_type', filters.exam_type);
      if (filters.difficulty_level) params.append('difficulty_level', filters.difficulty_level);

      const response = await axios.get(`${API_URL}/api/specific-exams/public?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExams(response.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los exámenes');
      console.error('Error fetching exams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/specific-exams/my-exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyExams(response.data);
    } catch (err) {
      console.error('Error fetching my exams:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleTakeExam = (examId) => {
    navigate(`/exam/specific/${examId}`);
  };

  const handleViewDetails = (examId) => {
    navigate(`/exam/details/${examId}`);
  };

  const handleDuplicateExam = async (examId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/specific-exams/${examId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Examen duplicado exitosamente');
      fetchMyExams();
    } catch (err) {
      alert('Error al duplicar el examen');
      console.error('Error duplicating exam:', err);
    }
  };

  const getExamTypeLabel = (type) => {
    const types = {
      official: 'Oficial',
      practice: 'Práctica',
      teacher: 'Profesor',
      custom: 'Personalizado',
    };
    return types[type] || type;
  };

  const getExamTypeColor = (type) => {
    const colors = {
      official: 'error',
      practice: 'primary',
      teacher: 'secondary',
      custom: 'default',
    };
    return colors[type] || 'default';
  };

  const getDifficultyLabel = (level) => {
    const levels = {
      easy: 'Fácil',
      medium: 'Medio',
      hard: 'Difícil',
      mixed: 'Mixto',
    };
    return levels[level] || level;
  };

  const getDifficultyColor = (level) => {
    const colors = {
      easy: 'success',
      medium: 'warning',
      hard: 'error',
      mixed: 'info',
    };
    return colors[level] || 'default';
  };

  const renderExamCard = (exam, showEditOptions = false) => (
    <Grid item xs={12} sm={6} md={4} key={exam.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Chip
              label={getExamTypeLabel(exam.exam_type)}
              color={getExamTypeColor(exam.exam_type)}
              size="small"
              icon={exam.exam_type === 'official' ? <EmojiEvents /> : <School />}
            />
            <Chip
              label={getDifficultyLabel(exam.difficulty_level)}
              color={getDifficultyColor(exam.difficulty_level)}
              size="small"
            />
          </Box>

          <Typography variant="h6" component="h2" gutterBottom>
            {exam.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {exam.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <QuestionAnswer fontSize="small" color="primary" />
              <Typography variant="body2">{exam.question_count} preguntas</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Timer fontSize="small" color="primary" />
              <Typography variant="body2">{exam.time_limit} min</Typography>
            </Box>
          </Box>

          {exam.tags && exam.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {exam.tags.map((tag, index) => (
                <Chip key={index} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          {exam.creator && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Creado por: {exam.creator.name}
            </Typography>
          )}

          {showEditOptions && (
            <Chip
              label={exam.status === 'published' ? 'Publicado' : 'Borrador'}
              color={exam.status === 'published' ? 'success' : 'default'}
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={() => handleTakeExam(exam.id)}
            fullWidth={!exam.is_template}
          >
            Tomar Examen
          </Button>

          {exam.is_template && (
            <IconButton
              color="secondary"
              onClick={() => handleDuplicateExam(exam.id)}
              title="Duplicar examen"
            >
              <ContentCopy />
            </IconButton>
          )}

          {showEditOptions && (
            <IconButton
              color="primary"
              onClick={() => navigate(`/teacher/edit-exam/${exam.id}`)}
              title="Editar examen"
            >
              <Visibility />
            </IconButton>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Exámenes Disponibles
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecciona un examen específico para practicar o genera uno rápido por área
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Exámenes Rápidos por Área */}
      <Card sx={{ mb: 4, p: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <School sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Exámenes Rápidos por Área
            </Typography>
            <Typography variant="body2">
              Genera un examen personalizado seleccionando temas específicos
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {[
            { id: 'matematicas', name: 'Matemáticas', color: '#e91e63' },
            { id: 'lectura-critica', name: 'Lectura Crítica', color: '#9c27b0' },
            { id: 'sociales', name: 'Sociales y Ciudadanas', color: '#3f51b5' },
            { id: 'ciencias', name: 'Ciencias', color: '#009688' },
            { id: 'ingles', name: 'Inglés', color: '#ff5722' },
          ].map((area) => (
            <Grid item xs={6} sm={4} md={2.4} key={area.id}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: 'white',
                  color: area.color,
                  fontWeight: 'bold',
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s',
                }}
                onClick={() => navigate(`/exam/${area.id}/config`)}
              >
                {area.name}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* Filtros */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList color="primary" />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Área"
              value={filters.area}
              onChange={(e) => handleFilterChange('area', e.target.value)}
            >
              {areas.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Tipo de Examen"
              value={filters.exam_type}
              onChange={(e) => handleFilterChange('exam_type', e.target.value)}
            >
              {examTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Dificultad"
              value={filters.difficulty_level}
              onChange={(e) => handleFilterChange('difficulty_level', e.target.value)}
            >
              {difficultyLevels.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs */}
      {(user.role === 'teacher' || user.role === 'admin') && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Exámenes Públicos" />
            <Tab label="Mis Exámenes" />
          </Tabs>
        </Box>
      )}

      {/* Lista de Exámenes */}
      {loading ? (
        <Typography>Cargando exámenes...</Typography>
      ) : (
        <>
          {currentTab === 0 && (
            <Grid container spacing={3}>
              {exams.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">No se encontraron exámenes con los filtros seleccionados</Alert>
                </Grid>
              ) : (
                exams.map((exam) => renderExamCard(exam))
              )}
            </Grid>
          )}

          {currentTab === 1 && (
            <Grid container spacing={3}>
              {myExams.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    No has creado ningún examen aún.{' '}
                    <Button onClick={() => navigate('/teacher/create-exam')}>Crear uno ahora</Button>
                  </Alert>
                </Grid>
              ) : (
                myExams.map((exam) => renderExamCard(exam, true))
              )}
            </Grid>
          )}
        </>
      )}

      {/* Botón para crear examen (solo profesores y admin) */}
      {(user.role === 'teacher' || user.role === 'admin') && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/teacher/create-exam')}
            sx={{ borderRadius: 8, px: 3, py: 1.5 }}
          >
            + Crear Nuevo Examen
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default ExamBrowser;
